/* Wave 3 — Content Studio panels.
 *
 * Three standalone panels exposed on the Creative Studio page:
 *   • CopywritingPanel — framework picker + brief + variant carousel
 *   • AdCreativePanel  — campaign/platform picker + char meters
 *   • CopyEditorPanel  — paste + goals → edited variants
 *
 * German UI copy; English code. */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { TemplateFallbackBanner } from "./TemplateFallbackBanner";

const FRAMEWORKS = ["CONVERSION_FIRST", "AIDA", "PAS", "BAB", "PSR",
                                "AUTHORITY", "TRUST", "SOCIAL_PROOF",
                                "URGENCY", "CLARITY"];

const KINDS = [
  ["landing_page_hero", "Landing Page Hero"],
  ["landing_page_cta", "Landing Page CTA"],
  ["email_subject", "E-Mail Betreff"],
  ["email_body", "E-Mail Body"],
  ["ad_headline", "Ad-Headline"],
  ["ad_primary_text", "Ad Primary Text"],
];

const CHANNELS = ["universal", "meta", "google", "linkedin",
                          "tiktok", "instagram", "facebook", "x"];

export function CopywritingPanel() {
  const [framework, setFramework] = useState("AIDA");
  const [kind, setKind] = useState("landing_page_hero");
  const [channel, setChannel] = useState("universal");
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [doc, setDoc] = useState(null);

  const generate = async () => {
    if (!brief || brief.length < 3) {
      toast.warning("Bitte mindestens einen kurzen Brief eingeben.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/content-studio/copywriting/generate",
        { framework, kind, channel, language: "de", brief, variants: 3 });
      setDoc(r.data);
      if (r.data.fallback_reason) {
        toast.warning(`Fallback: ${r.data.fallback_reason}`);
      } else {
        toast.success(`3 Varianten mit Framework ${framework} generiert.`);
      }
    } catch (e) {
      toast.error("Generierung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const approve = async (vid) => {
    try {
      await api.post(`/content-studio/copy-variants/${vid}/approve`);
      toast.success("Variante freigegeben");
      setDoc({ ...doc, status: "approved" });
    } catch (e) {
      const err = e?.response?.data?.detail;
      toast.error(err?.error || "Freigabe fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-4" data-testid="copywriting-panel">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Copywriting Generator</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Framework</Label>
              <Select value={framework} onValueChange={setFramework}>
                <SelectTrigger data-testid="copywriting-framework">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FRAMEWORKS.map((f) => (
                    <SelectItem key={f} value={f}
                                        data-testid={`framework-${f}`}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Kind</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="copywriting-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="copywriting-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNELS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Brief</Label>
            <Textarea rows={3} value={brief}
                          data-testid="copywriting-brief"
                          onChange={(e) => setBrief(e.target.value)}
                          placeholder="z.B. Kostenlose Immobilienbewertung Wuppertal" />
          </div>
          <Button onClick={generate} disabled={busy}
                      data-testid="copywriting-generate">
            {busy
              ? <Loader2 className="size-3 mr-1 animate-spin" />
              : <Sparkles className="size-3 mr-1" />}
            Generieren
          </Button>
        </CardContent>
      </Card>

      {doc && (
        <Card data-testid="copywriting-result">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              Varianten
              <Badge data-testid="copywriting-framework-badge">{doc.framework}</Badge>
              <Badge variant="outline">{doc.source}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {doc.source === "template_fallback" && (
              <TemplateFallbackBanner
                reason={doc.fallback_reason}
                testId="copywriting-fallback-banner"
              />
            )}
            {(doc.text_variants || []).map((v, i) => (
              <div key={v.id || i}
                      className="border rounded-md p-3 space-y-2"
                      data-testid={`copywriting-variant-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Var {i + 1} · {v.tone || "—"} · {v.hook || "—"}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={"text-[10px] " + (v.over_limit
                        ? "text-destructive" : "text-muted-foreground")}
                          data-testid={`copywriting-charcount-${i}`}>
                      {v.char_count}{v.over_limit
                        && <AlertTriangle className="size-3 inline ml-1" />}
                    </span>
                  </div>
                </div>
                <p className="text-sm whitespace-pre-wrap">{v.text}</p>
                {v.framework_notes && (
                  <p className="text-[10px] text-muted-foreground italic">
                    {v.framework_notes}
                  </p>
                )}
              </div>
            ))}
            <Button size="sm" onClick={() => approve(doc.id)}
                        disabled={doc.status === "approved"}
                        data-testid="copywriting-approve">
              {doc.status === "approved" ? "Freigegeben" : "Freigeben"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


export function AdCreativePanel() {
  const [framework, setFramework] = useState("URGENCY");
  const [platforms, setPlatforms] = useState(["meta", "google"]);
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);

  const togglePlatform = (p) => {
    setPlatforms((cur) => cur.includes(p)
      ? cur.filter((x) => x !== p)
      : [...cur, p]);
  };

  const generate = async () => {
    if (!brief || brief.length < 3 || platforms.length === 0) {
      toast.warning("Brief + mind. eine Plattform erforderlich.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/content-studio/ad-creative/generate",
        { framework, platforms, variants_per_platform: 2,
          brief, language: "de" });
      setItems(r.data.items || []);
      toast.success(`${r.data.count} Varianten für ${platforms.length} Plattform(en).`);
    } catch (e) {
      toast.error("Generierung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="ad-creative-panel">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Ad-Creative Multi-Plattform</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Framework</Label>
            <Select value={framework} onValueChange={setFramework}>
              <SelectTrigger data-testid="ad-framework">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FRAMEWORKS.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Plattformen</Label>
            <div className="flex flex-wrap gap-1"
                    data-testid="ad-platforms">
              {["meta", "google", "linkedin", "tiktok"].map((p) => (
                <button key={p} type="button"
                              onClick={() => togglePlatform(p)}
                              data-testid={`ad-platform-${p}`}
                              className={"px-2 py-1 rounded-md text-xs border "
                                + (platforms.includes(p)
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-transparent")}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Brief</Label>
            <Textarea rows={3} value={brief}
                          data-testid="ad-brief"
                          onChange={(e) => setBrief(e.target.value)}
                          placeholder="z.B. Kostenlose Bewertung, 48h Turnaround, NRW" />
          </div>
          <Button onClick={generate} disabled={busy}
                      data-testid="ad-generate">
            Generieren
          </Button>
        </CardContent>
      </Card>
      {items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3"
                data-testid="ad-results">
          {items.map((doc) => (
            <Card key={doc.id} data-testid={`ad-doc-${doc.channel}-${doc.kind}`}>
              <CardHeader>
                <CardTitle className="text-xs flex items-center gap-1">
                  <Badge>{doc.channel}</Badge>
                  <Badge variant="outline">{doc.kind}</Badge>
                  <Badge variant="outline">{doc.framework}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {(doc.text_variants || []).map((v, i) => (
                  <div key={i} className="border rounded-md p-2 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className={"text-[10px] " + (v.over_limit
                          ? "text-destructive" : "text-muted-foreground")}
                            data-testid={`ad-charcount-${doc.channel}-${doc.kind}-${i}`}>
                        {v.char_count}
                        {v.over_limit && (
                          <AlertTriangle className="size-3 inline ml-1" />
                        )}
                      </span>
                    </div>
                    <p className="text-xs">{v.text}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


export function CopyEditorPanel() {
  const [text, setText] = useState("");
  const [goals, setGoals] = useState(["clarity", "conversion"]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const toggleGoal = (g) => {
    setGoals((cur) => cur.includes(g)
      ? cur.filter((x) => x !== g)
      : [...cur, g]);
  };

  const review = async () => {
    if (!text) return;
    setBusy(true);
    try {
      const r = await api.post("/content-studio/copy-editing/review",
        { inline_text: text, goals, language: "de" });
      setResult(r.data);
    } catch (e) {
      toast.error("Review fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="copy-editor-panel">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Copy Editor</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Ausgangstext</Label>
            <Textarea rows={6} value={text}
                          data-testid="editor-text"
                          onChange={(e) => setText(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Ziele</Label>
            <div className="flex flex-wrap gap-1"
                    data-testid="editor-goals">
              {["clarity", "conversion", "tone_match", "shorten", "lengthen"].map((g) => (
                <button key={g} type="button"
                              onClick={() => toggleGoal(g)}
                              data-testid={`goal-${g}`}
                              className={"px-2 py-1 rounded-md text-xs border "
                                + (goals.includes(g)
                                    ? "bg-primary text-primary-foreground"
                                    : "")}>
                  {g}
                </button>
              ))}
            </div>
          </div>
          <Button onClick={review} disabled={busy || !text}
                      data-testid="editor-review">
            Review starten
          </Button>
        </CardContent>
      </Card>
      {result && (
        <Card data-testid="editor-result">
          <CardHeader>
            <CardTitle className="text-sm">
              Editierte Varianten <Badge>{result.source}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(result.edited_variants || []).map((v, i) => (
              <div key={i} className="border rounded-md p-2"
                      data-testid={`editor-variant-${i}`}>
                <p className="text-sm">{v.text}</p>
                {v.rationale && (
                  <p className="text-[10px] text-muted-foreground italic mt-1">
                    Begründung: {v.rationale}
                  </p>
                )}
              </div>
            ))}
            {result.improvements_summary && (
              <p className="text-xs text-muted-foreground mt-2"
                  data-testid="editor-summary">
                {result.improvements_summary}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
